(() => {
  const SELECTOR = '[data-lazy-section-id]';
  const loadedStyles = new Set(
    [...document.querySelectorAll('link[rel="stylesheet"][href]')].map((l) => l.href)
  );
  const loadedScripts = new Set([...document.querySelectorAll('script[src]')].map((s) => s.src));

  const loadStylesheet = (href) => {
    if (!href || loadedStyles.has(href)) return Promise.resolve();
    loadedStyles.add(href);
    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = resolve;
      document.head.appendChild(link);
    });
  };

  const loadScript = (src) => {
    if (!src || loadedScripts.has(src)) return Promise.resolve();
    loadedScripts.add(src);
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = resolve;
      script.onerror = resolve;
      document.body.appendChild(script);
    });
  };

  const hydrateSection = async (placeholder) => {
    const sectionId = placeholder.dataset.lazySectionId;
    if (!sectionId || placeholder.dataset.lazyLoading === 'true') return;
    placeholder.dataset.lazyLoading = 'true';

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) throw new Error(`Section ${sectionId} failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming =
        doc.getElementById(`shopify-section-${sectionId}`) || doc.querySelector('.shopify-section');
      if (!incoming) throw new Error(`Section ${sectionId} markup missing`);

      const styleHrefs = [...incoming.querySelectorAll('link[rel="stylesheet"][href]')].map((l) => l.href);
      const scriptSrcs = [...incoming.querySelectorAll('script[src]')].map((s) => s.src);
      const inlineScripts = [...incoming.querySelectorAll('script:not([src])')].map((s) => s.textContent || '');

      incoming.querySelectorAll('script').forEach((s) => s.remove());

      await Promise.all(styleHrefs.map(loadStylesheet));
      for (const src of scriptSrcs) {
        await loadScript(src);
      }

      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!current) return;
      current.replaceWith(incoming);

      // Run inline scripts inside the mounted section so patterns that rely on
      // document.currentScript / previousElementSibling keep working.
      const mounted = document.getElementById(`shopify-section-${sectionId}`) || incoming;
      inlineScripts.forEach((code) => {
        if (!code.trim()) return;
        const script = document.createElement('script');
        script.textContent = code;
        mounted.appendChild(script);
      });

      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId },
        })
      );
    } catch (error) {
      console.error('[lazy-sections]', error);
      placeholder.dataset.lazyLoading = 'false';
    }
  };

  const observe = () => {
    const placeholders = document.querySelectorAll(SELECTOR);
    if (!placeholders.length) return;

    if (!('IntersectionObserver' in window)) {
      placeholders.forEach((el) => hydrateSection(el));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrateSection(entry.target);
        });
      },
      { rootMargin: '400px 0px', threshold: 0.01 }
    );

    placeholders.forEach((el) => observer.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe, { once: true });
  } else {
    observe();
  }
})();
